import { useEffect, useMemo, useState } from "react"
import { EDeliveryStatusType } from "../../../Enum/EDeliveryStatusType"
import { DeliveryEntity } from "../../../Objects/DeliveryEntity/DeliveryEntity"
import { useTranslation } from "react-i18next"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { CourierRouteMap } from "./CourierRouteMap"

interface Props {
  delivery: DeliveryEntity
  onUpdateStatus: (id: number, status: EDeliveryStatusType) => void
  onCancelDelivery: (id: number) => void
}

const OFFER_DURATION_SECONDS = 60

function getSteps(t: (key: string) => string) {
  return [
    { value: EDeliveryStatusType.Offered, label: t("courier_card.steps.offered") },
    { value: EDeliveryStatusType.Accepted, label: t("courier_card.steps.accepted") },
    { value: EDeliveryStatusType.PickedUp, label: t("courier_card.steps.picked_up") },
    { value: EDeliveryStatusType.OnRoute, label: t("courier_card.steps.on_route") },
    { value: EDeliveryStatusType.Delivered, label: t("courier_card.steps.delivered") },
  ]
}

function getNextAction(status: EDeliveryStatusType, t: (key: string) => string) {
  switch (status) {
    case EDeliveryStatusType.Offered:
      return {
        primary: EDeliveryStatusType.Accepted,
        secondary: EDeliveryStatusType.Rejected,
        label: t("courier_card.actions.accept_delivery"),
        color: "bg-blue-600 hover:bg-blue-700",
      }

    case EDeliveryStatusType.Accepted:
      return {
        primary: EDeliveryStatusType.PickedUp,
        label: t("courier_card.actions.confirm_pickup"),
        color: "bg-amber-500 hover:bg-amber-600",
      }

    case EDeliveryStatusType.PickedUp:
      return {
        primary: EDeliveryStatusType.OnRoute,
        label: t("courier_card.actions.start_route"),
        color: "bg-indigo-600 hover:bg-indigo-700",
      }

    case EDeliveryStatusType.OnRoute:
      return {
        primary: EDeliveryStatusType.Delivered,
        label: t("courier_card.actions.confirm_delivery"),
        color: "bg-emerald-600 hover:bg-emerald-700",
      }

    default:
      return null
  }
}

export function CourierDeliveryCard({ delivery, onUpdateStatus, onCancelDelivery }: Props) {
  const { t } = useTranslation("frontend")

  const steps = getSteps(t)

  const action = getNextAction(delivery.status, t)

  const { setNewConfirmation } = useConfirmation()

  const dropoff = delivery.order?.addressEntity

  const phone = delivery.order?.customerEntity?.personEntity?.phone ?? ""

  const customerName = delivery.order?.customerEntity?.personEntity?.name ?? ""

  const total = delivery.order?.orderPaymentEntity?.totalFinal ?? 0

  const gain = delivery.order?.orderPaymentEntity?.shippingFee ?? 0

  const isPaid = delivery.order?.orderPaymentEntity?.isPaid?.() ?? false

  const paymentType = delivery.order?.orderPaymentEntity.ePaymentType.toPaymentTypeString()

  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null)

  useEffect(() => {
    if (delivery.status !== EDeliveryStatusType.Offered || !delivery.offerExpiresDateTime) {
      setRemainingSeconds(null)
      return
    }

    const calculate = () => {
      const diff = new Date(delivery.offerExpiresDateTime!).getTime() - new Date().getTime()

      if (diff <= 0) {
        setRemainingSeconds(0)
        return false
      }

      setRemainingSeconds(Math.floor(diff / 1000))
      return true
    }

    const shouldContinue = calculate()

    if (!shouldContinue) return

    const interval = setInterval(() => {
      const stillValid = calculate()
      if (!stillValid) clearInterval(interval)
    }, 1000)

    return () => clearInterval(interval)
  }, [delivery.offerExpiresDateTime, delivery.status])

  const timerPercentage = useMemo(() => {
    if (remainingSeconds === null || delivery.status !== EDeliveryStatusType.Offered) return 0

    return Math.max((remainingSeconds / OFFER_DURATION_SECONDS) * 100, 0)
  }, [remainingSeconds, delivery.status])

  const currentStepIndex = steps.findIndex((s) => s.value === delivery.status)

  const progressPercentage = (currentStepIndex / (steps.length - 1)) * 100

  const openGoogleMaps = () => {
    const url = `https://www.google.com/maps/dir/?api=1&origin=${delivery.pickupLatitude},${delivery.pickupLongitude}&destination=${delivery.dropoffLatitude},${delivery.dropoffLongitude}`
    window.open(url, "_blank")
  }

  const canCancelDelivery = [
    EDeliveryStatusType.Accepted,
    EDeliveryStatusType.PickedUp,
    EDeliveryStatusType.OnRoute,
  ].includes(delivery.status)

  const handleCopyText = async (value: string) => {
    if (!value) {
      return
    }

    try {
      await navigator.clipboard.writeText(value)
    } catch {
      const textArea = document.createElement("textarea")
      textArea.value = value
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand("copy")
      document.body.removeChild(textArea)
    }
  }

  return (
    <article className="bg-white rounded-md shadow-sm p-2 border-2 border-slate-200 space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-stretch">
        <div className="order-1 w-full aspect-square md:aspect-auto md:h-[300px]">
          <CourierRouteMap
            pickupLatitude={delivery.pickupLatitude}
            pickupLongitude={delivery.pickupLongitude}
            dropoffLatitude={delivery.dropoffLatitude}
            dropoffLongitude={delivery.dropoffLongitude}
          />
        </div>

        <div className="order-2 space-y-1.5">
          <div className="grid grid-cols-2 gap-1.5 text-xs">
            <div>
              <div className="flex flex-row">
                <p className="text-[12px] uppercase font-bold tracking-wide text-slate-500">
                  {t("courier_card.delivery")}
                </p>

                {isPaid && <strong className="ml-5 font-extrabold text-green-600">{" * Devendo * "}</strong>}
              </div>

              <h2 className="text-[15px] font-semibold text-slate-900 leading-tight">{customerName}</h2>

              <strong className="font-extrabold text-red-600">{paymentType}</strong>
            </div>

            <div className="text-right border-slate-200 min-w-20">
              <p className="text-[12px] font-bold text-slate-500">{t("delivery_common.you_receive")}</p>

              <p className="text-lg font-bold text-emerald-600">{gain.toMoney()}</p>
            </div>
          </div>

          <div className="rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-[11px] leading-relaxed text-slate-700">
            Quem paga pela entrega é a loja responsável. O app apenas registra a operação, e o direito ao
            recebimento existe somente para pedidos realmente entregues.
          </div>

          <div className="grid grid-cols-2 gap-1 text-xs">
            <div className="flex w-full flex-row justify-between border-2 border-slate-200 rounded p-2">
              <div className="text-start border-slate-200 min-w-20">
                <p className="text-slate-500">{t("courier_card.distance")}</p>

                <p className="font-semibold text-slate-800">{(delivery.estimatedDistanceKm ?? 0).toFixed(2)} km</p>
              </div>

              <div className="text-right border-slate-200 min-w-20">
                <p className="text-[12px] font-bold text-slate-500">{t("delivery_common.total_order")}</p>

                <p className="text-lg font-bold text-red-600">{total.toMoney()}</p>
              </div>
            </div>

            <div className="border-2 border-slate-200 rounded p-1 text-right">
              <p className="text-slate-500">{t("courier_card.contact")}</p>

              <p className="font-semibold text-slate-800 text-[13px] truncate">
                {phone || t("courier_card.not_informed")}
              </p>

              {!!phone && (
                <button
                  onClick={() => handleCopyText(phone)}
                  className="text-[11px] text-blue-600 font-semibold hover:underline"
                  data-interaction-description-key="interactionDescriptions:pages.delivery.components.courierDeliveryCard.button.handleCopyTextPhone"
                >
                  {t("courier_card.copy")}
                </button>
              )}
            </div>
          </div>

          <div className="border-2 border-slate-200 rounded p-2">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-[12px] font-bold text-slate-500 mb-1">{t("courier_card.destination")}</p>

                <p className="font-semibold text-slate-800 text-sm leading-tight">
                  {dropoff?.street}, {dropoff?.number}
                </p>

                <p className="text-xs text-slate-600">
                  {dropoff?.neighborhood} • {dropoff?.city}
                </p>
              </div>

              <button
                onClick={() =>
                  handleCopyText(
                    `${dropoff?.street ?? ""}, ${dropoff?.number ?? ""} - ${dropoff?.neighborhood ?? ""} - ${dropoff?.city ?? ""}`,
                  )
                }
                className="text-[11px] text-blue-600 font-semibold hover:underline whitespace-nowrap"
                data-interaction-description-key="interactionDescriptions:pages.delivery.components.courierDeliveryCard.button.handleCopyText"
              >
                {t("courier_card.copy")}
              </button>
            </div>
          </div>

          {action && (delivery.status !== EDeliveryStatusType.Offered || remainingSeconds !== 0) && (
            <div className="pt-1.5 space-y-2">
              <button
                onClick={() => onUpdateStatus(delivery.id, action.primary)}
                className={`w-full py-3 rounded-md text-white font-semibold text-sm transition ${action.color}`}
                data-interaction-description-key={
                  action.primary === EDeliveryStatusType.Accepted
                    ? "interactionDescriptions:pages.delivery.components.courierDeliveryCard.button.acceptDelivery"
                    : action.primary === EDeliveryStatusType.PickedUp
                      ? "interactionDescriptions:pages.delivery.components.courierDeliveryCard.button.confirmPickup"
                      : action.primary === EDeliveryStatusType.OnRoute
                        ? "interactionDescriptions:pages.delivery.components.courierDeliveryCard.button.startRoute"
                        : "interactionDescriptions:pages.delivery.components.courierDeliveryCard.button.confirmDelivery"
                }
              >
                {action.label}
              </button>

              <div className="flex flex-row gap-2">
                {action.secondary && (
                  <button
                    onClick={() => onUpdateStatus(delivery.id, action.secondary!)}
                    className="w-full py-2 ring-2 ring-red-200 rounded-md text-red-600 font-semibold hover:bg-red-50 transition text-sm"
                    data-interaction-description-key="interactionDescriptions:pages.delivery.components.courierDeliveryCard.button.onUpdateStatusDeliveryIdActionSecondary"
                  >
                    {t("courier_card.reject")}
                  </button>
                )}

                {canCancelDelivery && (
                  <button
                    onClick={() =>
                      setNewConfirmation(
                        t("courier_card.cancel_delivery_title"),
                        t("courier_card.cancel_delivery_confirm"),
                        () => onCancelDelivery(delivery.id),
                      )
                    }
                    className="w-full py-2 rounded-md ring-2 ring-red-500 text-red-600 font-semibold  hover:bg-red-50 transition text-sm"
                    data-interaction-description-key="interactionDescriptions:pages.delivery.components.courierDeliveryCard.button.cancelDeliveryTitle"
                  >
                    {t("courier_card.cancel_delivery")}
                  </button>
                )}

                <button
                  onClick={openGoogleMaps}
                  className="w-full py-2 rounded-md ring-2 ring-gray-600 text-slate-700 font-semibold border border-slate-300 hover:bg-slate-50 transition text-sm"
                  data-interaction-description-key="interactionDescriptions:pages.delivery.components.courierDeliveryCard.button.openGoogleMaps"
                >
                  {t("courier_card.open_google_maps")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {remainingSeconds !== null && delivery.status === EDeliveryStatusType.Offered && remainingSeconds > 0 && (
        <div>
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span>{t("courier_card.offer_expires_in")}</span>

            <span className="font-semibold text-red-500">{remainingSeconds}s</span>
          </div>

          <div className="w-full bg-red-100 h-1.5 rounded overflow-hidden">
            <div className="h-full bg-red-500 transition-all duration-1000" style={{ width: `${timerPercentage}%` }} />
          </div>
        </div>
      )}

      <div className="pt-0.5">
        <div className="relative">
          <div className="absolute top-3 left-0 right-0 h-1 bg-slate-200 rounded" />

          <div
            className="absolute top-3 left-0 h-1 bg-emerald-500 rounded transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          />

          <div className="relative flex justify-between">
            {steps.map((step, index) => (
              <div key={step.label} className="flex flex-col items-center w-11">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${
                    index <= currentStepIndex
                      ? "bg-emerald-500 text-white"
                      : "bg-white border border-slate-300 text-slate-400"
                  }`}
                >
                  {index + 1}
                </div>

                <span
                  className={`text-[10px] mt-1 text-center ${
                    index <= currentStepIndex ? "text-emerald-600 font-semibold" : "text-slate-400"
                  }`}
                >
                  {step.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {!action && (
        <div className="text-center text-emerald-600 font-semibold text-sm">{t("courier_card.delivery_completed")}</div>
      )}
    </article>
  )
}
